/**
 * Multi-Document Signal Creation Wizard
 * 
 * A 3-step wizard for uploading multiple documents, merging extractions,
 * and creating a single draft signal with all files attached as evidence.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiCheckCircle, FiX, FiUpload, FiLoader, FiAlertCircle, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import IconWrapper from '../../IconWrapper';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useMultiDocumentSignal } from '../../../hooks/useMultiDocumentSignal';
import { useDatasets } from '../../../hooks/useDatasets';
import { createSimulation } from '../../../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../../contexts/NavigationContext';
import { cn } from '../../../lib/utils';
import type { EditableSignal } from '../../../types/multi-document';

interface MultiDocumentSignalWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (signalId: string) => void;
  datasetId?: string;
  defaultRunSimulation?: boolean;
}

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const FileUploadZone: React.FC<{
  onFilesAdded: (files: File[]) => void;
  disabled?: boolean;
}> = ({ onFilesAdded, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const validTypes = [
    'text/plain',
    'text/csv',
    'application/csv',
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'message/rfc822',
  ];

  const validateFiles = (files: FileList): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} is too large (max 10MB)`);
      } else if (
        !validTypes.includes(file.type) &&
        !file.name.toLowerCase().endsWith('.txt') &&
        !file.name.toLowerCase().endsWith('.csv')
      ) {
        errors.push(`${file.name} is not a supported file type`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      toast.error(errors.join(', '));
    }

    return validFiles;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        onFilesAdded(validFiles);
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        onFilesAdded(Array.from(validFiles));
      }
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  return (
    <div
      className={cn(
        'border-2 border-dashed rounded-lg p-8 transition-colors',
        disabled
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
          : isDragging
          ? 'border-primary bg-primary/5 cursor-pointer'
          : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 cursor-pointer'
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={handleDrop}
      onClick={() => {
        if (!disabled) fileInputRef.current?.click();
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,.csv,.eml,.msg,.jpg,.jpeg,.png,.gif,.webp,.xls,.xlsx"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
        aria-label="File upload input"
      />
      <div className="flex flex-col items-center text-center">
        <IconWrapper icon={FiUpload} className="w-8 h-8 text-muted-foreground mb-2" />
        <p className="text-sm font-medium text-foreground mb-1">
          Drop files here or{' '}
          <span className="text-primary hover:underline">browse</span>
        </p>
        <p className="text-xs text-muted-foreground">
          PDF, Word, Text, CSV, Email, or Image files (max 10MB each, up to {MAX_FILES} files)
        </p>
      </div>
    </div>
  );
};

export const MultiDocumentSignalWizard: React.FC<MultiDocumentSignalWizardProps> = ({
  open,
  onClose,
  onSuccess,
  datasetId: initialDatasetId,
  defaultRunSimulation = false,
}) => {
  const navigate = useNavigate();
  const { navigateWithReferrer } = useNavigation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | undefined>(initialDatasetId);
  const [editedSignal, setEditedSignal] = useState<EditableSignal | null>(null);
  const [runSimulation, setRunSimulation] = useState(defaultRunSimulation);

  const { data: datasetsResponse } = useDatasets();
  const datasets = datasetsResponse?.datasets || [];

  const {
    processFiles,
    finalize,
    isProcessing,
    processingProgress,
    completedFiles,
    totalFiles,
    hasErrors,
    fileProcessingStatus,
    signalId,
    extractedData,
    warnings,
    errors,
    isSaving,
    isLoading,
    error,
    createDraftMutation,
  } = useMultiDocumentSignal({
    onSuccess: (id) => {
      toast.success('Signal created successfully');
      if (onSuccess) {
        onSuccess(id);
      }
      onClose();
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to create signal');
    },
  });

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setFiles([]);
      setEditedSignal(null);
      setRunSimulation(defaultRunSimulation);
      setSelectedDatasetId(initialDatasetId);
    }
  }, [open, initialDatasetId]);

  // Update edited signal when extracted data arrives
  useEffect(() => {
    if (extractedData && step === 2) {
      setEditedSignal(extractedData);
    }
  }, [extractedData, step]);

  // Auto-advance to step 2 when processing completes
  useEffect(() => {
    if (!isProcessing && completedFiles === totalFiles && totalFiles > 0 && step === 1) {
      // extractedData is the EditableSignal, not the full response
      // Check if we have signalId instead
      if (signalId) {
        setStep(2);
      }
    }
  }, [isProcessing, completedFiles, totalFiles, signalId, step]);

  const handleFilesAdded = useCallback(
    (newFiles: File[]) => {
      const combined = [...files, ...newFiles].slice(0, MAX_FILES);
      setFiles(combined);
    },
    [files]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      setFiles((prev) => prev.filter((_, i) => i !== index));
    },
    []
  );

  const handleProcessFiles = useCallback(async () => {
    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    try {
      // selectedDatasetId is already undefined if "No collection" was selected
      await processFiles(files, selectedDatasetId);
    } catch (err) {
      // Error handled by hook
    }
  }, [files, selectedDatasetId, processFiles]);

  const handleSave = useCallback(async () => {
    if (!signalId) {
      toast.error('No signal to save');
      return;
    }

    try {
      // Finalize the signal
      await finalize(signalId);
      toast.success('Signal finalized successfully');

      // Optionally create simulation and navigate to it
      if (runSimulation) {
        try {
          const simulationResponse = await createSimulation(signalId);
          if (simulationResponse?.id) {
            toast.success('Case guidance started');
            // Navigate to case page (like the old dropzone did)
            navigateWithReferrer(`/v3/ombudsman/simulations/${simulationResponse.id}`, '/v3/ombudsman');
            // Call onSuccess callback and close wizard
            if (onSuccess) {
              onSuccess(signalId);
            }
            onClose();
            return;
          } else {
            throw new Error('Case guidance creation failed - no ID returned');
          }
        } catch (simError: any) {
          toast.warning(`Signal saved but case guidance failed: ${simError.message}`);
        }
      }

      // If no simulation, call onSuccess callback and close wizard
      if (onSuccess) {
        onSuccess(signalId);
      }
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save signal');
    }
  }, [signalId, runSimulation, finalize, navigateWithReferrer, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    if (isProcessing) {
      if (!window.confirm('Processing is in progress. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  }, [isProcessing, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-background w-full max-w-6xl h-[90vh] overflow-hidden rounded-lg border shadow-lg relative mx-6 my-10 md:mx-12 flex flex-col">
        {/* Close Button */}
        <button
          aria-label="Close"
          className="absolute right-6 top-6 text-gray-400 hover:text-gray-600 text-3xl leading-none p-1.5 z-10"
          onClick={handleClose}
        >
          <IconWrapper icon={FiX} className="h-6 w-6" />
        </button>
        <div className="flex h-full flex-1 min-h-0">
          {/* Stepper Sidebar */}
          <aside className="w-80 border-r border-gray-200 p-10 bg-white">
            <h2 className="text-2xl font-semibold mb-8">Start from Documents</h2>
            <ol className="space-y-12">
              {[
                { n: 1, title: 'Upload Documents' },
                { n: 2, title: 'Review & Edit' },
                { n: 3, title: 'Confirm & Save' },
              ].map((s) => (
                <li key={s.n} className="flex items-start relative">
                  <div className="relative w-6 mr-3 mt-1">
                    <span
                      className={cn(
                        'inline-flex items-center justify-center w-6 h-6 text-xs rounded-full border-2',
                        step === s.n
                          ? 'bg-go-primary border-go-primary text-white'
                          : step > s.n
                          ? 'bg-go-primary/10 border-go-primary text-go-primary'
                          : 'bg-white border-go-primary text-go-primary'
                      )}
                    >
                      {step > s.n ? (
                        <IconWrapper icon={FiCheckCircle} className="w-4 h-4 text-go-primary" />
                      ) : (
                        s.n
                      )}
                    </span>
                    {s.n < 3 && (
                      <span
                        className={cn(
                          'absolute left-1/2 -translate-x-1/2 top-[44px] w-0.5 h-[56px]',
                          step > s.n ? 'bg-go-primary' : 'bg-gray-200'
                        )}
                      />
                    )}
                  </div>
                  <div>
                    <div
                      className={cn(
                        step === s.n ? 'text-go-primary font-semibold' : 'text-go-primary font-medium'
                      )}
                    >
                      {s.title}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </aside>

          {/* Content Area */}
          <div className="flex-1 p-10 overflow-y-auto">
            {/* Step 1: Upload Documents */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Upload Documents</h3>
                </div>

                {/* Dataset Selection */}
                {datasets.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="dataset-select">Collection (Optional)</Label>
                    <Select 
                      value={selectedDatasetId || '__none__'} 
                      onValueChange={(value) => {
                        setSelectedDatasetId(value === '__none__' ? undefined : value);
                      }}
                    >
                      <SelectTrigger id="dataset-select">
                        <SelectValue placeholder="Select a collection" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No collection</SelectItem>
                        {datasets.map((dataset) => (
                          <SelectItem key={dataset.id} value={dataset.id}>
                            {dataset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* File Upload Zone */}
                <FileUploadZone
                  onFilesAdded={handleFilesAdded}
                  disabled={files.length >= MAX_FILES || isProcessing}
                />

                {/* File List */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    <Label>Uploaded Files ({files.length}/{MAX_FILES})</Label>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {files.map((file, index) => {
                        const status = fileProcessingStatus.get(file.name);
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 border rounded-lg bg-card"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {status?.status === 'processing' && (
                                <IconWrapper icon={FiLoader} className="h-4 w-4 animate-spin text-primary" />
                              )}
                              {status?.status === 'completed' && (
                                <IconWrapper icon={FiCheckCircle} className="h-4 w-4 text-green-600" />
                              )}
                              {status?.status === 'error' && (
                                <IconWrapper icon={FiAlertCircle} className="h-4 w-4 text-destructive" />
                              )}
                              {(!status || status.status === 'pending') && (
                                <IconWrapper icon={FiLoader} className="h-4 w-4 text-muted-foreground" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{file.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-xs text-muted-foreground">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </p>
                                  {status && (
                                    <Badge 
                                      variant={
                                        status.status === 'completed' 
                                          ? 'default' 
                                          : status.status === 'error' 
                                          ? 'destructive' 
                                          : 'secondary'
                                      }
                                      className="text-xs"
                                    >
                                      {status.status === 'completed' 
                                        ? 'Completed' 
                                        : status.status === 'processing' 
                                        ? 'Processing' 
                                        : status.status === 'error' 
                                        ? 'Failed' 
                                        : 'Pending'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {!isProcessing && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveFile(index)}
                                className="h-8 w-8 p-0"
                              >
                                <IconWrapper icon={FiX} className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {warnings.length > 0 && (
                  <Alert>
                    <IconWrapper icon={FiAlertCircle} className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        {warnings.map((warning, i) => (
                          <div key={i}>{warning}</div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Errors */}
                {errors.length > 0 && (
                  <Alert variant="destructive">
                    <IconWrapper icon={FiAlertCircle} className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        {errors.map((err, i) => (
                          <div key={i}>{err}</div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Processing Progress Overlay */}
                {isProcessing && (
                  <Card className="fixed bottom-4 right-4 w-96 shadow-lg z-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <IconWrapper icon={FiLoader} className="h-5 w-5 animate-spin text-primary" />
                          <h4 className="text-sm font-semibold">Processing Documents</h4>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Progress value={processingProgress} className="h-2" />
                        <p className="text-xs text-muted-foreground text-center">
                          {completedFiles} of {totalFiles} completed
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Navigation */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProcessFiles}
                    disabled={files.length === 0 || isProcessing || isLoading}
                  >
                    {isProcessing ? 'Processing...' : 'Process Files'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Review & Edit */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Review & Edit</h3>
                  <p className="text-sm text-muted-foreground">
                    Review the merged signal data and make any necessary edits.
                  </p>
                </div>

                {editedSignal && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="customer-name">Customer Name</Label>
                        <Input
                          id="customer-name"
                          value={editedSignal.customer_name || ''}
                          onChange={(e) =>
                            setEditedSignal({ ...editedSignal, customer_name: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customer-email">Customer Email</Label>
                        <Input
                          id="customer-email"
                          type="email"
                          value={editedSignal.customer_email || ''}
                          onChange={(e) =>
                            setEditedSignal({ ...editedSignal, customer_email: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="verbatim">Verbatim</Label>
                      <Textarea
                        id="verbatim"
                        value={editedSignal.verbatim || ''}
                        onChange={(e) =>
                          setEditedSignal({ ...editedSignal, verbatim: e.target.value })
                        }
                        rows={6}
                      />
                    </div>


                    {/* Source Badges */}
                    {createDraftMutation.data?.source_extractions && createDraftMutation.data.source_extractions.length > 0 && (
                      <div className="space-y-2">
                        <Label>Source Documents</Label>
                        <div className="flex flex-wrap gap-2">
                          {createDraftMutation.data.source_extractions.map((extraction: any, index: number) => (
                            <Badge
                              key={index}
                              variant={extraction.success ? 'secondary' : 'destructive'}
                            >
                              {extraction.filename}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <IconWrapper icon={FiChevronLeft} className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={() => setStep(3)}>
                    Continue
                    <IconWrapper icon={FiChevronRight} className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Confirm & Save */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Confirm & Save</h3>
                  <p className="text-sm text-muted-foreground">
                    Review the summary and save your signal.
                  </p>
                </div>

                {/* Summary Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Signal Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Customer:</span>
                      <span className="ml-2 font-medium">{editedSignal?.customer_name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Evidence Files:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {files.map((file, index) => (
                          <Badge key={index} variant="secondary">
                            {file.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Simulation Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-card">
                  <div className="space-y-0.5">
                    <Label htmlFor="run-simulation" className="text-sm font-medium">
                      Run FOS Simulation
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically create a simulation after saving this signal
                    </p>
                  </div>
                  <Switch
                    id="run-simulation"
                    checked={runSimulation}
                    onCheckedChange={setRunSimulation}
                  />
                </div>

                {/* Navigation */}
                <div className="flex justify-between gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <IconWrapper icon={FiChevronLeft} className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Signal'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
